import React, { useContext, useState } from "react";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import IOnGetInventoryStockHistoryTransactionReportDTO from "../../../../models/DTO/IOnGetInventoryStockHistoryTransactionReportDTO";
import IOnGetInventoryCostBreakdownReportDTO from "../../../../models/DTO/IOnGetInventoryCostBreakdownReportDTO";
import styled from "styled-components";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import UserService from "../../../../services/UserService";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTranslate from "../../../shared/OwTranslate/OwTranslate";
import { NumericFormat } from "react-number-format";
import SupplierContext from "../../../../contexts/SupplierContext";
import ExcelJS from "exceljs/dist/exceljs.min.js";
import OwButton from "../../../shared/OwButton/OwButton";
import UIService from "../../../../services/UIService";

type Props = {
    processedReport: IProcessed_Report;
};

export const InventoryCostBreakdownReport = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    const [reportData, setReportData] = useState<IOnGetInventoryCostBreakdownReportDTO>(
        props.processedReport.report_data as IOnGetInventoryCostBreakdownReportDTO
    );

    const downloadExcel = () => {
        if (reportData) {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet(`Inventory Cost Breakdown Report`);

            const columns: (
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
            )[] = [
                { header: "Name", key: "name" },
                { header: "Qty. On Hand", key: "qty_on_hand" },
                { header: "Cost / UOM", key: "cost_per_uom" },
                { header: "Ext. Cost", key: "ext_cost" },
            ];

            ws.columns = columns;

            ws.getCell("A1").font = {
                bold: true,
            };

            ws.getCell("B1").font = {
                bold: true,
            };

            ws.getCell("C1").font = {
                bold: true,
            };

            ws.getCell("D1").font = {
                bold: true,
            };

            for (const categoryName of Object.keys(reportData.data.categories)) {
                const categoryRow = ws.addRow({ name: categoryName });
                categoryRow.font = {
                    bold: true,
                };

                for (const item of reportData.data.categories[categoryName].items) {
                    const name = item.name;
                    const qty_on_hand = item.qty_on_hand;
                    const cost_per_uom = Number(UIService.roundNumber(item.cost_per_uom, supplierModel.pricing_precision));
                    const ext_cost = Number(UIService.roundNumber(item.sub_total, supplierModel.pricing_precision));

                    ws.addRow({ name, qty_on_hand, cost_per_uom, ext_cost });
                }

                const subTotalRow = ws.addRow({
                    qty_on_hand: Number(
                        UIService.roundNumber(
                            reportData.data.categories[categoryName].qty_on_hand_sub_total,
                            supplierModel.pricing_precision
                        )
                    ),
                    ext_cost: Number(
                        UIService.roundNumber(reportData.data.categories[categoryName].sub_total, supplierModel.pricing_precision)
                    ),
                });
                subTotalRow.font = {
                    bold: true,
                };
                subTotalRow.border = {
                    top: {
                        style: "medium",
                    },
                };
                ws.addRow({});
            }

            const totalRow = ws.addRow({
                qty_on_hand: reportData.data.totals.total_qty_on_hand,
                ext_cost: reportData.data.totals.grand_total,
            });
            totalRow.font = {
                bold: true,
            };
            totalRow.border = {
                top: {
                    style: "thick",
                },
            };

            // const periodStart = moment(reportData.periodStart).format("YYYY-MM-DD");
            // const periodEnd = moment(reportData.periodEnd).format("YYYY-MM-DD");

            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `orderwerks-inventory-cost-breakdown-${UserService.parseLastUpdated(
                    props.processedReport.created_at
                )}.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    return (
        <div className={"InventoryCostBreakdownReport"}>
            {reportData && reportData.data.totals.grand_total > 0 ? (
                <OwButton
                    leftIconClass={"fad fa-file-excel"}
                    className={"btn-primary d-print-none"}
                    title={"export_to_excel"}
                    fn={downloadExcel}
                />
            ) : null}
            <div>
                <OwTitle title={"inventory_cost_breakdown"} tag={"h3"} />
                <OwLabel title={"created_at"} postText={":"} className={"me-2"} />
                {UserService.parseLastUpdated(props.processedReport.created_at)}
            </div>

            <table className={"table table-sm table-striped " + (UIService.isMainViewLight() ? "table-light" : "table-dark")}>
                <thead>
                    <tr>
                        <th>
                            <OwTranslate defaultMessage={"name"} id={"name"} />
                        </th>
                        <th className={"text-end"}>
                            <OwTranslate defaultMessage={"qty_on_hand"} id={"qty_on_hand"} />
                        </th>
                        <th className={"text-end"}>
                            <OwTranslate defaultMessage={"cost_per_uom"} id={"cost_per_uom"} />
                        </th>
                        <th className={"text-end"}>
                            <OwTranslate defaultMessage={"ext_cost"} id={"ext_cost"} />
                        </th>
                    </tr>
                </thead>
                {Object.keys(reportData?.data?.categories).map((key, index) => {
                    // @ts-expect-error
                    const category = reportData?.data.categories[key];

                    return (
                        <TableBodyWrapper key={index}>
                            <tr>
                                <th colSpan={4}>{category?.category}</th>
                            </tr>

                            {category?.items.map((item) => {
                                return (
                                    <tr key={item.sku}>
                                        <td>{item?.name}</td>
                                        <td className={"text-end"}>{item?.qty_on_hand}</td>
                                        <td className={"text-end"}>
                                            <NumericFormat
                                                value={item?.cost_per_uom}
                                                displayType={"text"}
                                                thousandSeparator={true}
                                                decimalScale={supplierModel.pricing_precision || 2}
                                                fixedDecimalScale={true}
                                            />
                                        </td>
                                        <td className={"text-end"}>
                                            <NumericFormat
                                                value={item?.sub_total}
                                                displayType={"text"}
                                                thousandSeparator={true}
                                                decimalScale={supplierModel.pricing_precision || 2}
                                                fixedDecimalScale={true}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                            <SubTotalTr>
                                <td colSpan={2} className={"text-end"}>
                                    <NumericFormat
                                        value={category?.qty_on_hand_sub_total}
                                        displayType={"text"}
                                        thousandSeparator={true}
                                        decimalScale={supplierModel.pricing_precision || 2}
                                        fixedDecimalScale={true}
                                    />
                                </td>
                                <td colSpan={2} className={"text-end"}>
                                    <NumericFormat
                                        value={category?.sub_total}
                                        displayType={"text"}
                                        thousandSeparator={true}
                                        decimalScale={supplierModel.pricing_precision || 2}
                                        fixedDecimalScale={true}
                                    />
                                </td>
                            </SubTotalTr>
                        </TableBodyWrapper>
                    );
                })}
                <TableBodyWrapper>
                    <GrandTotalTr>
                        <td colSpan={2} className={"text-end"}>
                            <NumericFormat
                                value={reportData?.data?.totals.total_qty_on_hand}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                            />
                        </td>
                        <td colSpan={2} className={"text-end"}>
                            <NumericFormat
                                value={reportData?.data?.totals.grand_total}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                            />
                        </td>
                    </GrandTotalTr>
                </TableBodyWrapper>
            </table>
        </div>
    );
};

const TableBodyWrapper = styled.tbody`
    &::before {
        content: "";
        display: block;
        height: 2rem;
    }
`;

const SubTotalTr = styled.tr`
    border-top: 2px solid;
    font-weight: bold;
`;

const GrandTotalTr = styled.tr`
    border-top: 3px solid;
    font-weight: bold;
`;
